import { useState, useEffect } from 'react';
import { Box, LinearProgress, Typography, Fade, Slide } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AeDriveLogo from '../assets/images/AeDriveLogo.png';

const SessionValidation = ({ session, onValidationComplete }) => {
  const theme = useTheme();
  const [progress, setProgress] = useState(0);
  const [validating, setValidating] = useState(true);
  const [validationResult, setValidationResult] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (!session) {
      onValidationComplete();
      return;
    }

    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return 90;
        }
        return prev + 10;
      });
    }, 200);

    const validateDevice = async () => {
      try {
        const userAgent = navigator.userAgent || '';
        let os = 'Unknown';
        if (userAgent.includes('Windows')) os = 'Windows';
        else if (userAgent.includes('Mac')) os = 'macOS';
        else if (userAgent.includes('Linux')) os = 'Linux';
        else if (userAgent.includes('Android')) os = 'Android';
        else if (userAgent.includes('iOS') || userAgent.includes('iPhone') || userAgent.includes('iPad')) os = 'iOS';
        
        let browser = 'Unknown';
        if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) browser = 'Chrome';
        else if (userAgent.includes('Firefox')) browser = 'Firefox';
        else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) browser = 'Safari';
        else if (userAgent.includes('Edg')) browser = 'Edge';
        else if (userAgent.includes('Opera') || userAgent.includes('OPR')) browser = 'Opera';
        
        const screenResolution = `${window.screen.width}x${window.screen.height}`;
        const currentDeviceId = `${os}_${browser}_${screenResolution}`;
        const opusDeviceId = session.deviceId;
        
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        const isValid = opusDeviceId === currentDeviceId;
        
        setProgress(100);
        setValidating(false);
        setValidationResult({
          isValid,
          username: session.username,
        });
        
        if (isValid) {
          setTimeout(() => {
            setShowSuccess(true);
            setTimeout(() => {
              onValidationComplete();
            }, 3000);
          }, 500);
        } else {
          setTimeout(() => {
            onValidationComplete();
          }, 2000);
        }
      } catch (error) {
        console.error('Error validating device:', error);
        setProgress(100);
        setValidating(false);
        setTimeout(() => {
          onValidationComplete();
        }, 1000);
      }
    };

    validateDevice();

    return () => {
      clearInterval(progressInterval);
    };
  }, [session, onValidationComplete]);

  if (!session) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: theme.palette.background.default,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
      }}
    >
      <Fade in={true} timeout={800}>
        <Box
          component="img"
          src={AeDriveLogo}
          alt="AE-Drive Logo"
          sx={{
            width: { xs: 200, sm: 280 },
            height: 'auto',
            mb: 6,
            opacity: validating ? 1 : 0.7,
            transition: 'opacity 0.5s ease',
          }}
        />
      </Fade>

      {validating && (
        <Fade in={validating} timeout={500}>
          <Box sx={{ width: '100%', maxWidth: 400, px: 4 }}>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 3,
                  backgroundColor: theme.palette.primary.main,
                },
              }}
            />
          </Box>
        </Fade>
      )}
      {showSuccess && validationResult?.isValid && (
        <Fade in={showSuccess} timeout={1000}>
          <Box
            sx={{
              mt: 6,
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Slide direction="up" in={showSuccess} timeout={800}>
              <Typography
                variant="h4"
                sx={{
                  color: theme.palette.white,
                  fontWeight: 700,
                  fontSize: { xs: '1.5rem', sm: '2rem' },
                  letterSpacing: '0.05em',
                }}
              >
                Device Validated
              </Typography>
            </Slide>
            <Slide direction="up" in={showSuccess} timeout={1000}>
              <Typography
                variant="h6"
                sx={{
                  color: theme.palette.text.primary,
                  fontWeight: 500,
                  fontSize: { xs: '1rem', sm: '1.25rem' },
                  letterSpacing: '0.02em',
                }}
              >
                for AE-Drive
              </Typography>
            </Slide>
            {validationResult.username && (
              <Slide direction="up" in={showSuccess} timeout={1200}>
                <Typography
                  variant="body1"
                  sx={{
                    color: theme.palette.secondary.main,
                    fontWeight: 400,
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    mt: 1,
                    letterSpacing: '0.05em',
                  }}
                >
                  {validationResult.username}
                </Typography>
              </Slide>
            )}
          </Box>
        </Fade>
      )}
    </Box>
  );
};

export default SessionValidation;

